import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../../store';
import { deleteLead, fetchLeads, selectLeadPagination, selectLeadSummary } from '../../../store/slices/leadSlice';
import { fetchLeadSourceOptions, selectLeadSourceLoaded, selectLeadSourceOptions } from '../../../store/slices/leadSourceSlice';
import { FALLBACK_LEAD_SOURCE_OPTIONS } from '../../../data/leadSourceOptions';
import { FiBarChart2, FiSearch, FiEdit2, FiTrash2, FiPhone, FiMoreVertical } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import PageWrapper from '../../common/PageWrapper';
import AddLeadModal from '../../modules/Lead/AddLeadModal';
import WhatsAppMessageModal from '../../modules/WhatsApp/WhatsAppMessageModal';
import Pagination from '../../../../../components/common/Pagination';
import { DEFAULT_PAGINATION } from '../../../../../constants/pagination';
import './EnquiryPage.css';
import { PlusIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import type { Lead } from '../../../types';
import {
  Button,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@mui/material';
import { useLeadStages } from '../../../hooks/useLeadStages';
import { capitalizeWords } from '../../../../../utils/formatters';

const EnquiryPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { leads, loading } = useAppSelector((state) => state.leads);
  const leadSourceOptions = useAppSelector(selectLeadSourceOptions);
  const leadSourceLoaded = useAppSelector(selectLeadSourceLoaded);
  const pagination = useAppSelector(selectLeadPagination);
  const { stages, getStageLabel } = useLeadStages();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState<Lead | null>(null);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [selectedLeadForWhatsApp, setSelectedLeadForWhatsApp] = useState<{
    phone: string;
    name?: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGINATION.page);
  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_PAGINATION.limit);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSource, selectedStatus, activeTab]);

  useEffect(() => {
    // Only use API status filter for legacy status values (from tabs)
    // Dynamic stage filtering is done client-side
    const statusFilter = activeTab !== "all"
      ? (activeTab === "active" ? undefined : activeTab)
      : undefined; // Don't pass selectedStatus to API - filter client-side instead

    dispatch(fetchLeads({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
      status: statusFilter
    }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm, selectedStatus, activeTab]); // Keep selectedStatus in deps to trigger re-fetch when it changes

  useEffect(() => {
    if (!leadSourceLoaded) {
      dispatch(fetchLeadSourceOptions());
    }
  }, [dispatch, leadSourceLoaded]);

  // Filter leads based on active tab
  const getFilteredLeads = () => {
    let filtered = leads;

    // Filter by tab
    if (activeTab === 'active') {
      filtered = filtered.filter((l) => l.status === 'contacted' || l.status === 'qualified');
    } else if (activeTab === 'qualified') {
      filtered = filtered.filter((l) => l.status === 'qualified');
    } else if (activeTab === 'converted') {
      filtered = filtered.filter((l) => l.status === 'converted');
    } else if (activeTab === 'lost') {
      filtered = filtered.filter((l) => l.status === 'lost');
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (l) =>
          l?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          l?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          l?.phone?.includes(searchTerm) ||
          (l.company && l.company.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by source
    if (selectedSource) {
      filtered = filtered.filter((l) => l.source === selectedSource);
    }

    // Filter by status/stage - check detailedStage first, then fall back to status
    if (selectedStatus) {
      filtered = filtered.filter((l) =>
        l.detailedStage === selectedStatus || l.status === selectedStatus
      );
    }

    return filtered;
  };

  const filteredLeads = getFilteredLeads();

  const summary = useAppSelector(selectLeadSummary);

  // Calculate counts using summary from backend
  const allCount = summary?.total || 0;
  const activeCount = summary?.active || 0;
  const qualifiedCount = summary?.qualified || 0;
  const convertedCount = summary?.converted || 0;
  const lostCount = summary?.lost || 0;

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      dispatch(deleteLead(id));
    }
  };

  const [openMenuLeadId, setOpenMenuLeadId] = useState<string | null>(null);

  const toggleActionMenu = (leadId: string) => {
    setOpenMenuLeadId((prev) => (prev === leadId ? null : leadId));
  };

  const startPhoneCall = (phone: string) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const openWhatsAppForLead = (lead: Lead) => {
    if (!lead.phone) return;
    setSelectedLeadForWhatsApp({
      phone: lead.phone,
      name: (lead as any).contactPersonName || lead.name,
    });
    setIsWhatsAppModalOpen(true);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'new':
        return '#3b82f6';
      case 'contacted':
        return '#8b5cf6';
      case 'qualified':
        return '#10b981';
      case 'converted':
        return '#059669';
      case 'lost':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const uniqueSources = Array.from(
    new Set([
      ...FALLBACK_LEAD_SOURCE_OPTIONS,
      ...leadSourceOptions,
      ...leads.map((l) => l.source).filter(Boolean),
    ])
  );
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);

  // Column headers for lead table
  const tableColumns = [
    '#',
    'LEAD NAME',
    'SOURCE',
    'CREATED AT',
    'LAST UPDATED',
    'STAGE',
    'ACTIONS',
  ];

  return (
    <PageWrapper>
      <div className="enquiry-page">
        <div className="page-header">
          <div className="header-left">
            <div className="page-title-section">
              <FiBarChart2 className="title-icon" />
              <h1 className="page-title">Leads</h1>
            </div>
            <p className="page-description">
              Manage and track all your leads across different stages
            </p>
          </div>
          <button
            className="add-new-btn"
            type="button"
            onClick={() => setIsAddLeadModalOpen(true)}
          >
            <PlusIcon width={20} height={20} />
            Add New Lead
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Leads <span className="tab-count">({allCount})</span>
          </button>
          {/* <button
            className={`tab-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active <span className="tab-count">({activeCount})</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'qualified' ? 'active' : ''}`}
            onClick={() => setActiveTab('qualified')}
          >
            Qualified <span className="tab-count">({qualifiedCount})</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'converted' ? 'active' : ''}`}
            onClick={() => setActiveTab('converted')}
          >
            Converted <span className="tab-count">({convertedCount})</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'lost' ? 'active' : ''}`}
            onClick={() => setActiveTab('lost')}
          >
            Lost <span className="tab-count">({lostCount})</span>
          </button> */}
        </div>

        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FiSearch className="h-4 w-4" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Source</InputLabel>
                <Select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  label="Source"
                  displayEmpty
                  renderValue={(value) => (value ? value : 'All Sources')}
                >
                  <MenuItem value="">All Sources</MenuItem>
                  {uniqueSources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Stage</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Stage"
                  displayEmpty
                  renderValue={(value) => (value ? value : 'All Stages')}
                >
                  <MenuItem value="">All Stages</MenuItem>
                  {stages && stages.length > 0 ? (
                    stages
                      .filter(stage =>
                        stage.isActive &&
                        stage.key !== 'contacted' &&
                        stage.key !== 'followup' &&
                        stage.key !== 'followUp'
                      )
                      .sort((a, b) => a.stageOrder - b.stageOrder)
                      .map((stage) => (
                        <MenuItem key={stage.key} value={stage.key}>
                          {stage.label}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem value="" disabled>Loading stages...</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setSearchTerm('');
                  setSelectedSource('');
                  setSelectedStatus('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <div className="table-container">
          <table className="enquiries-table">
            <thead>
              <tr>
                {tableColumns.map((column, index) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No leads found
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead, index) => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="id-cell">{String((currentPage - 1) * itemsPerPage + index + 1).padStart(4, '0')}</td>
                    <td className="name-cell">
                      <div className="name-with-avatar">
                        <div className="avatar-circle">
                          {((lead as any).type === 'business') ? (
                            <BuildingOfficeIcon className="h-5 w-5 text-white" />
                          ) : (
                            <UserIcon className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div>
                          <div className="enquiry-name">
                            {capitalizeWords(lead.name)}
                          </div>
                          <div className="enquiry-email-small">
                            {capitalizeWords(lead.siteName) || capitalizeWords(lead.company) || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{lead.source}</td>
                    <td>
                      <div>
                        {/* <div className="enquiry-name">
                          {lead.salesperson || lead.assignedSalesperson || '—'}
                        </div> */}
                        <div className="enquiry-email-small">
                          {lead.createdAt
                            ? new Date(lead.createdAt).toLocaleDateString()
                            : '—'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        {/* <div className="enquiry-name">
                          {lead.assignedSalesperson || lead.salesperson || '—'}
                        </div> */}
                        <div className="enquiry-email-small">
                          {lead.updatedAt
                            ? new Date(lead.updatedAt).toLocaleDateString()
                            : '—'}
                        </div>
                      </div>
                    </td>
                    <td>
                      {/* <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusBadgeColor(lead.status) }}
                      >
                        {lead.currentStage}
                      </span> */}
                      <Chip
                        label={lead.currentStage.toUpperCase()}
                        size="medium"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </td>
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="text"
                        onClick={() => openWhatsAppForLead(lead)}
                        sx={{
                          color: '#25D366',
                          minWidth: 'auto',
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(37, 211, 102, 0.1)',
                          },
                        }}
                      >
                        <FaWhatsapp size={20} />
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => startPhoneCall(lead.phone)}
                        sx={{
                          color: '#0284c7',
                          minWidth: 'auto',
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 132, 199, 0.1)',
                          },
                        }}
                      >
                        <FiPhone size={20} />
                      </Button>
                      <div className="menu-wrapper">
                        <Button
                          variant="text"
                          onClick={() => toggleActionMenu(lead.id)}
                          sx={{
                            color: '#6b7280',
                            minWidth: 'auto',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(107, 114, 128, 0.1)',
                            },
                          }}
                        >
                          <FiMoreVertical size={20} />
                        </Button>
                        {/* <button
                          className="action-btn menu-btn"
                          title="More actions"
                          onClick={() => toggleActionMenu(lead.id)}
                        >
                          <FiMoreVertical />
                        </button> */}
                        {openMenuLeadId === lead.id && (
                          <div className="action-menu" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="action-menu-item"
                              onClick={() => {
                                setSelectedLeadForEdit(lead);
                                setIsAddLeadModalOpen(true);
                                setOpenMenuLeadId(null);
                              }}
                            >
                              <FiEdit2 className="action-menu-icon" />
                              Edit
                            </button>
                            <button
                              type="button"
                              className="action-menu-item action-menu-item-danger"
                              onClick={() => {
                                handleDelete(lead.id);
                                setOpenMenuLeadId(null);
                              }}
                            >
                              <FiTrash2 className="action-menu-icon" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onItemsPerPageChange={(limit) => {
              setItemsPerPage(limit);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onRequestClose={() => {
          setIsAddLeadModalOpen(false);
          setSelectedLeadForEdit(null);
        }}
        initialData={selectedLeadForEdit ?? undefined}
        mode="lead"
      />

      {selectedLeadForWhatsApp && (
        <WhatsAppMessageModal
          isOpen={isWhatsAppModalOpen}
          onRequestClose={() => {
            setIsWhatsAppModalOpen(false);
            setSelectedLeadForWhatsApp(null);
          }}
          phone={selectedLeadForWhatsApp.phone}
          contactName={selectedLeadForWhatsApp.name}
        />
      )}
    </PageWrapper>
  );
};

export default EnquiryPage;

